import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { BdGraphApiUser } from '@bindoc-projects/bindoc-permissions-lib/data';
import { AvatarModule } from 'primeng/avatar';
import { Tooltip } from 'primeng/tooltip';

/**
 * Text that identifies a user. `displayName` is nullable in the backend
 * `UserDto` (accounts that were never named), so fall back to the e-mail and
 * finally the username — never `null`, which PrimeNG's autocomplete would
 * crash on when it lower-cases the option label.
 */
export function userLabel(user: BdGraphApiUser): string {
  return (
    user.displayName || user.loginEmail || user.mail || user.username || ''
  );
}

@Component({
  selector: 'bd-user-search-item',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex items-center gap-3">
      <p-avatar [label]="initials()" shape="circle"></p-avatar>
      <div class="flex-1 min-w-0">
        @if (isAdmin()) {
          <div class="flex items-center gap-2 truncate">
            <span class="font-medium truncate">{{ label() }}</span>
            @if (user().userType === 'Guest') {
              <i class="pi pi-id-card" pTooltip="Gast"></i>
            }
            @if (!user().isAccountEnabled) {
              <i class="pi pi-lock" pTooltip="Konto deaktiviert"></i>
            }
          </div>
          @if (user().createdDateTime) {
            <div class="text-xs text-gray-400">
              {{ email() }} · {{ user().id }} · angelegt am
              {{ user().createdDateTime | date: 'dd.MM.yyyy' }}
            </div>
          }
        } @else {
          <div class="font-medium truncate">{{ label() }}</div>
          <div class="text-sm text-muted-color truncate">{{ email() }}</div>
        }
      </div>
    </div>
  `,
  imports: [DatePipe, Tooltip, AvatarModule],
})
export class BdUserSearchItemComponent {
  public readonly user = input.required<BdGraphApiUser>();
  public readonly isAdmin = input(false);

  public readonly label = computed(() => userLabel(this.user()));

  public readonly initials = computed(() => {
    return this.label()
      .split(/\s+/)
      .filter((p) => p.length > 0)
      .slice(0, 2)
      .map((p) => p.charAt(0).toUpperCase())
      .join('');
  });

  public readonly email = computed(
    () => this.user().loginEmail || this.user().mail,
  );
}
